import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { PieChartInternal } from './components/PieChartInternal.js';
import { CHART_VARIANT, PIE_CHART_ARIA_LABEL } from './constants.js';
import { usePieChartConfig } from './hooks/usePieChartConfig.js';
import {
  ChartProviders,
  type ChartProvidersProps,
} from './providers/ChartProviders.js';
import { Grouping as PieChartGrouping } from './slots/PieChartGrouping.js';
import { Labels as PieChartLabels } from './slots/PieChartLabels.js';
import { Legend as PieChartLegend } from './slots/PieChartLegend.js';
import type { PieChartRef } from './types/pie-chart-ref.js';
import type { PieChartProps } from './types/pie-chart.config.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ChartType } from '../core/types/chart-types.js';

const PieChartRoot = forwardRef<PieChartRef, PropsWithChildren<PieChartProps>>(
  (props, forwardedRef) => {
    const {
      className,
      style,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-testid': dataTestId = 'pie-chart-root',
    } = props;

    const chartConfig = usePieChartConfig(props);

    const { emptyState, errorState } = getChartStatesTemplates(props.children);

    const isDataAvailable = !isEmpty(props.data);

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs<PieChartRef | HTMLDivElement>([
      forwardedRef,
      containerRef,
    ]);

    useImperativeHandle(
      forwardedRef,
      (): PieChartRef => ({
        element: containerRef.current,
        getConfig: () =>
          JSON.stringify({
            type: ChartType.Pie,
            ...chartConfig,
          }),
        downloadData: () => {
          buildAndDownloadCsv(props.data);
        },
      }),
    );

    const providersProps: ChartProvidersProps = {
      chartData: props.data,
      chartConfig,
    };

    return (
      <ChartProviders {...providersProps}>
        <PieChartInternal
          {...props}
          ref={rootRef}
          height={chartConfig.height}
          variant={CHART_VARIANT.Pie}
          isEmpty={!isDataAvailable}
          emptyState={emptyState}
          errorState={errorState}
          className={clsx(defaultChartSize, className)}
          style={style}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          data-testid={dataTestId}
          aria-label={PIE_CHART_ARIA_LABEL}
        />
      </ChartProviders>
    );
  },
);

(PieChartRoot as typeof PieChartRoot & { displayName: string }).displayName =
  'PieChart';

/**
 * The `PieChart` visualizes data proportions in a circular chart.
 * It divides a circle into slices, with each slice representing a specific
 * category or value.
 * @public
 */
export const PieChart = Object.assign(PieChartRoot, {
  Legend: PieChartLegend,
  Grouping: PieChartGrouping,
  Labels: PieChartLabels,
  Intent,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
